import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { isNetworkFailure } from '@/lib/networkFailure';
import { collectEmbedded } from '@/lib/mcMembership';

interface GoogleAccount {
  id: string;
  email: string;
  display_name: string;
}

interface MerchantCenter {
  id: string;
  merchant_center_id: string;
  name: string;
  /** Nullable in der Datenbank (`merchant_centers.website_url`), und der lokale
   *  Typ sagt es seit dem Typecheck-Gate auch. Aus DIESEM Context liest das Feld
   *  niemand — `MerchantCenters.tsx` fuehrt seine eigene Zeilenform. */
  website_url: string | null;
  // GMC-access-lifecycle (see migration 20260516120000_merchant_center_access_lifecycle.sql).
  // Consumed by useMcAccessStatus to drive the blocking modal + persistent banner.
  access_status: 'active' | 'permission_denied';
  last_permission_error_at: string | null;
  scheduled_deletion_at: string | null;
  deletion_overridden_at: string | null;
  access_revoked_modal_dismissed_at: string | null;
}

/** `merchant_centers.access_status` traegt in der Datenbank einen
 *  CHECK-Constraint auf ('active','permission_denied'); der generierte Typ kennt
 *  davon nichts und sagt nur `string` — eine CHECK-Union kann der Typegen nicht
 *  ableiten. Statt eines blinden Casts wird der Wert hier geprueft. Die
 *  Zuordnung ist beweisbar laufzeitneutral: die beiden Leser (`useJourney`,
 *  `FeedHealthDashboard`) vergleichen ausschliesslich `=== 'permission_denied'`,
 *  und fuer jeden anderen Wert war dieser Vergleich schon vorher falsch. */
function toAccessStatus(value: string): MerchantCenter['access_status'] {
  return value === 'permission_denied' ? 'permission_denied' : 'active';
}

interface Feed {
  id: string;
  feed_id: string;
  display_name: string;
  target_country: string;
  content_language: string;
  data_source_name: string | null;
  estimated_product_count: number | null;
  /** Zeitpunkt des letzten erfolgreichen Product-Syncs. `useJourney` unterscheidet
   *  daran einen frisch verbundenen, nie gesynchten Feed von einem leeren —
   *  ohne dieses Feld zeigte die Leiste am allerersten Tag "alles erledigt". */
  last_synced_at: string | null;
  /** Fallback fuer die Sync-Frische-Anzeige (`ContextSelector.tsx`-Formel:
   *  `last_synced_at || updated_at`), wenn noch nie automatisch gesynct wurde.
   *  Von `FeedSwitcher` fuer die geschlossene Pille genutzt (Task 5). */
  updated_at: string | null;
}

interface SelectionContextType {
  selectedGoogleAccountId: string | null;
  selectedMerchantCenterId: string | null;
  selectedFeedId: string | null;
  
  setSelectedGoogleAccount: (id: string) => Promise<void>;
  setSelectedMerchantCenter: (id: string) => Promise<void>;
  setSelectedFeed: (id: string) => Promise<void>;
  /** Atomically set the full account→MC→feed chain in one write (no cascading nulls). */
  setSelection: (googleAccountId: string, merchantCenterId: string, feedId: string) => Promise<void>;
  
  googleAccount: GoogleAccount | null;
  merchantCenter: MerchantCenter | null;
  /** true, sobald loadMerchantCenter fuer die AKTUELLE Auswahl abgeschlossen
   *  ist — egal ob mit Ergebnis oder mit Fehler/0 Rows. Unterscheidet "noch
   *  nicht aufgeloest" (merchantCenter null, resolved false → Ladezustand
   *  zeigen) von "aufgeloest, aber fehlend" (merchantCenter null, resolved
   *  true → die MC existiert nicht mehr oder war nicht ladbar; Konsumenten
   *  wie useJourney rendern dann die wahre Aussage hasMerchantCenter=false
   *  statt eines ewigen Skeletons). Ohne dieses Signal blieb die eine
   *  Fortschrittsleiste nach einer serverseitig geloeschten MC (oder einem
   *  einzigen Netzfehler beim Kaltstart) fuer den Rest der Session ein
   *  Skeleton — loadMerchantCenter laeuft nur bei ID-Wechsel erneut. */
  merchantCenterResolved: boolean;
  feed: Feed | null;

  loading: boolean;
  refreshSelection: () => Promise<void>;
  clearSelection: () => Promise<void>;
}

const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within SelectionProvider');
  }
  return context;
};

interface SelectionProviderProps {
  children: ReactNode;
}

// Helper to read cached selection from localStorage
// Cache includes userId to prevent cross-user data leaks when sessions change
const getCachedSelection = (userId: string | null) => {
  try {
    const cached = localStorage.getItem('feed-optimizer-selection');
    if (cached) {
      const parsed = JSON.parse(cached);
      // Invalidate cache if it belongs to a different user
      if (parsed.userId && parsed.userId !== userId) {
        localStorage.removeItem('feed-optimizer-selection');
        return null;
      }
      return parsed;
    }
  } catch (e) { console.warn('Failed to read cached selection:', e); }
  return null;
};

const saveCachedSelection = (userId: string | null, googleId: string | null, merchantId: string | null, feedId: string | null) => {
  try {
    localStorage.setItem('feed-optimizer-selection', JSON.stringify({
      userId: userId,
      googleAccountId: googleId,
      merchantCenterId: merchantId,
      feedId: feedId,
    }));
  } catch (e) { console.warn('Failed to save cached selection:', e); }
};

export const SelectionProvider: React.FC<SelectionProviderProps> = ({ children }) => {
  const { user } = useAuth();

  // Initialize from localStorage cache for instant restore — validated against current user
  const cached = getCachedSelection(user?.id ?? null);
  const [selectedGoogleAccountId, setSelectedGoogleAccountIdState] = useState<string | null>(cached?.googleAccountId || null);
  const [selectedMerchantCenterId, setSelectedMerchantCenterIdState] = useState<string | null>(cached?.merchantCenterId || null);
  const [selectedFeedId, setSelectedFeedIdState] = useState<string | null>(cached?.feedId || null);

  const [googleAccount, setGoogleAccount] = useState<GoogleAccount | null>(null);
  const [merchantCenter, setMerchantCenter] = useState<MerchantCenter | null>(null);
  // Startet false: bei gecachter Selection laeuft der Lade-Effect unten sofort
  // an; ohne selektierte MC setzt derselbe Effect das Flag im else-Zweig.
  const [merchantCenterResolved, setMerchantCenterResolved] = useState(false);
  const [feed, setFeed] = useState<Feed | null>(null);

  // Start as not-loading if we have cached data (instant render)
  const [loading, setLoading] = useState(!cached?.feedId);

  // Track previous user to detect user switches (e.g. session expired + new login)
  const prevUserIdRef = React.useRef<string | null>(null);

  // Load initial selection from database — and reset on user switch or logout
  useEffect(() => {
    if (!user) {
      // User logged out — immediately clear ALL selection state
      setSelectedGoogleAccountIdState(null);
      setSelectedMerchantCenterIdState(null);
      setSelectedFeedIdState(null);
      setGoogleAccount(null);
      setMerchantCenter(null);
      setFeed(null);
      localStorage.removeItem('feed-optimizer-selection');
      prevUserIdRef.current = null;
      setLoading(true);
      return;
    }

    // Detect user switch: different user logged in without explicit signOut
    if (prevUserIdRef.current && prevUserIdRef.current !== user.id) {
      // User switched! Clear stale selection from previous user
      setSelectedGoogleAccountIdState(null);
      setSelectedMerchantCenterIdState(null);
      setSelectedFeedIdState(null);
      setGoogleAccount(null);
      setMerchantCenter(null);
      setFeed(null);
      localStorage.removeItem('feed-optimizer-selection');
      setLoading(true);
    } else {
      const cachedNow = getCachedSelection(user.id);
      if (!cachedNow && prevUserIdRef.current !== user.id) {
        // First load with no valid cache — clear state inherited from stale cache
        setSelectedGoogleAccountIdState(null);
        setSelectedMerchantCenterIdState(null);
        setSelectedFeedIdState(null);
      }
    }
    prevUserIdRef.current = user.id;
    loadSelection();
  }, [user]);

  // Load details when IDs change
  useEffect(() => {
    if (selectedGoogleAccountId) {
      loadGoogleAccount(selectedGoogleAccountId);
    } else {
      setGoogleAccount(null);
    }
  }, [selectedGoogleAccountId]);

  useEffect(() => {
    if (selectedMerchantCenterId) {
      // Neuer Ladelauf fuer eine (andere) MC: bis er abgeschlossen ist, ist
      // die Auswahl "noch nicht aufgeloest" — nicht "fehlend".
      setMerchantCenterResolved(false);
      loadMerchantCenter(selectedMerchantCenterId);
    } else {
      // Keine MC selektiert: es gibt nichts aufzuloesen.
      setMerchantCenter(null);
      setMerchantCenterResolved(true);
    }
  }, [selectedMerchantCenterId]);

  useEffect(() => {
    if (selectedFeedId) {
      loadFeed(selectedFeedId);
    } else {
      setFeed(null);
    }
  }, [selectedFeedId]);

  // Trigger counter — incremented by refreshSelection so auto-select re-runs
  const [autoSelectTrigger, setAutoSelectTrigger] = useState(0);

  // Auto-select logic: runs after load and whenever refreshSelection triggers it
  useEffect(() => {
    if (!user || loading) return;
    autoSelectDefaults();
  }, [user, loading, autoSelectTrigger]);

  const autoSelectDefaults = async () => {
    try {
      let googleId = selectedGoogleAccountId;
      let merchantId = selectedMerchantCenterId;
      let feedId = selectedFeedId;

      // Auto-select Google Account if none selected — pick most recent
      if (!googleId) {
        const { data: accounts } = await supabase
          .from('google_accounts')
          .select('id')
          .order('created_at', { ascending: false })
          .limit(1);

        if (accounts?.length) {
          googleId = accounts[0].id;
        } else {
          // No accounts exist — clear stale cached selection (e.g. after account deletion)
          setSelectedGoogleAccountIdState(null);
          setSelectedMerchantCenterIdState(null);
          setSelectedFeedIdState(null);
          localStorage.removeItem('feed-optimizer-selection');
          return;
        }
      }

      // Auto-select Merchant Center if none selected — prefer one with feeds.
      // Mitgliedschafts-Umstellung (Task 10): "MCs dieses Google-Kontos" laeuft
      // ueber merchant_center_members statt direkt ueber merchant_centers.
      // `!inner` filtert Mitgliedschaftszeilen ohne sichtbare MC weg.
      if (!merchantId) {
        const { data } = await supabase
          .from('merchant_center_members')
          .select('merchant_centers!inner(id, name, feeds(id))')
          .eq('google_account_id', googleId);

        const rows = data ?? [];
        // Flatten/Dedupe-Kern liegt seit Fix-Runde 1 (Important 1) in
        // `collectEmbedded` (`lib/mcMembership.ts`), statt hier ein weiteres
        // Mal dieselbe Schleife zu tragen.
        const merchants = collectEmbedded(
          rows,
          (row) => row.merchant_centers,
          (_row, mc) => mc.id,
        ).map(({ embed }) => embed);
        // Client-seitig alphabetisch sortiert, wie es vorher der Server tat
        // (`.order('name', { ascending: true })` auf merchant_centers) --
        // traegt die deterministische Reihenfolge des Fallbacks unten.
        merchants.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

        if (merchants.length) {
          const withFeeds = merchants.find((m) => (m.feeds?.length ?? 0) > 0);
          merchantId = withFeeds?.id || merchants[0].id;
        }
      }

      // Auto-select Feed if none selected and merchant is known — pick first
      if (!feedId && merchantId) {
        const { data: feeds } = await supabase
          .from('feeds')
          .select('id')
          .eq('merchant_center_id', merchantId)
          .neq('feed_type', 'supplemental')
          .order('display_name', { ascending: true })
          .limit(1);

        if (feeds?.length) {
          feedId = feeds[0].id;
        }
      }

      // Apply all changes at once to avoid cascading re-renders and race conditions
      const changed = googleId !== selectedGoogleAccountId ||
                      merchantId !== selectedMerchantCenterId ||
                      feedId !== selectedFeedId;
      if (changed) {
        setSelectedGoogleAccountIdState(googleId);
        setSelectedMerchantCenterIdState(merchantId || null);
        setSelectedFeedIdState(feedId || null);
        saveCachedSelection(user?.id ?? null, googleId, merchantId || null, feedId || null);
        await saveSelection(googleId, merchantId || null, feedId || null);
      }
    } catch (error) {
      console.error('Auto-select error:', error);
      // Ein Netzwerkausfall des Browsers (Tab offline, Crawler ohne Verbindung)
      // ist kein Befund -- siehe lib/networkFailure.ts (DENO-J).
      if (!isNetworkFailure(error)) {
        Sentry.captureException(error, { tags: { feature: 'selection', action: 'auto-select' } });
      }
    }
  };

  const loadSelection = async () => {
    try {
      // Don't show loading spinner if we have cached data
      if (!cached?.feedId) setLoading(true);
      
      const { data, error } = await supabase
        .from('user_selections')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setSelectedGoogleAccountIdState(data.google_account_id);
        setSelectedMerchantCenterIdState(data.merchant_center_id);
        setSelectedFeedIdState(data.feed_id);
        saveCachedSelection(user?.id ?? null, data.google_account_id, data.merchant_center_id, data.feed_id);
      }
    } catch (error) {
      console.error('Error loading selection:', error);
      // "TypeError: Failed to fetch" aus HeadlessChrome/offline Tabs ist kein
      // Befund, den ein Codepfad beheben koennte (DENO-J, 4 Events, 0 Nutzer).
      if (!isNetworkFailure(error)) {
        Sentry.captureException(error, { tags: { feature: 'selection', action: 'load' } });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleAccount = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('google_accounts')
        .select('id, email, display_name')
        .eq('id', id)
        .single();

      if (error) throw error;
      setGoogleAccount(data);
    } catch (error) {
      console.error('Error loading Google account:', error);
      Sentry.captureException(error, { tags: { feature: 'selection', action: 'load-google-account' } });
      setGoogleAccount(null);
    }
  };

  const loadMerchantCenter = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('merchant_centers')
        .select('id, merchant_center_id, name, website_url, access_status, last_permission_error_at, scheduled_deletion_at, deletion_overridden_at, access_revoked_modal_dismissed_at')
        .eq('id', id)
        .single();

      if (error) throw error;
      setMerchantCenter({ ...data, access_status: toAccessStatus(data.access_status) });
    } catch (error) {
      console.error('Error loading merchant center:', error);
      Sentry.captureException(error, { tags: { feature: 'selection', action: 'load-merchant-center' } });
      setMerchantCenter(null);
    } finally {
      // Auch der Fehlerfall ist eine Aufloesung: `.single()` auf eine
      // geloeschte Row oder ein transienter Netzfehler duerfen keinen
      // session-dauerhaften "laedt noch"-Zustand hinterlassen.
      setMerchantCenterResolved(true);
    }
  };

  const loadFeed = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('feeds')
        .select('id, feed_id, display_name, target_country, content_language, data_source_name, estimated_product_count, last_synced_at, updated_at')
        .eq('id', id)
        .single();

      if (error) throw error;
      setFeed(data);
    } catch (error) {
      console.error('Error loading feed:', error);
      Sentry.captureException(error, { tags: { feature: 'selection', action: 'load-feed' } });
      setFeed(null);
    }
  };

  const saveSelection = async (googleId: string | null, merchantId: string | null, feedId: string | null) => {
    try {
      const { error } = await supabase.functions.invoke('save-user-selection', {
        body: {
          googleAccountId: googleId,
          merchantCenterId: merchantId,
          feedId: feedId,
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving selection:', error);
      Sentry.captureException(error, { tags: { feature: 'selection', action: 'save' } });
    }
  };

  const setSelectedGoogleAccount = async (id: string) => {
    setSelectedGoogleAccountIdState(id);
    setSelectedMerchantCenterIdState(null);
    setSelectedFeedIdState(null);
    saveCachedSelection(user?.id ?? null, id, null, null);
    await saveSelection(id, null, null);
  };

  const setSelectedMerchantCenter = async (id: string) => {
    setSelectedMerchantCenterIdState(id);
    setSelectedFeedIdState(null);
    saveCachedSelection(user?.id ?? null, selectedGoogleAccountId, id, null);
    await saveSelection(selectedGoogleAccountId, id, null);
  };

  const setSelectedFeed = async (id: string) => {
    setSelectedFeedIdState(id);
    saveCachedSelection(user?.id ?? null, selectedGoogleAccountId, selectedMerchantCenterId, id);
    await saveSelection(selectedGoogleAccountId, selectedMerchantCenterId, id);
  };

  // Atomic full-chain setter — avoids the stale-closure issue of calling the three
  // setters in sequence (each reads its parent id from the render closure).
  const setSelection = async (googleAccountId: string, merchantCenterId: string, feedId: string) => {
    setSelectedGoogleAccountIdState(googleAccountId);
    setSelectedMerchantCenterIdState(merchantCenterId);
    setSelectedFeedIdState(feedId);
    saveCachedSelection(user?.id ?? null, googleAccountId, merchantCenterId, feedId);
    await saveSelection(googleAccountId, merchantCenterId, feedId);
  };

  const refreshSelection = async () => {
    await loadSelection();
    // Re-trigger auto-select so newly created data gets picked up (e.g. after wizard)
    setAutoSelectTrigger((t) => t + 1);
  };

  const clearSelection = async () => {
    setSelectedGoogleAccountIdState(null);
    setSelectedMerchantCenterIdState(null);
    setSelectedFeedIdState(null);
    localStorage.removeItem('feed-optimizer-selection');
    await saveSelection(null, null, null);
    // Re-trigger auto-select so it picks the next available account
    setAutoSelectTrigger((t) => t + 1);
  };

  return (
    <SelectionContext.Provider
      value={{
        selectedGoogleAccountId,
        selectedMerchantCenterId,
        selectedFeedId,
        setSelectedGoogleAccount,
        setSelectedMerchantCenter,
        setSelectedFeed,
        setSelection,
        googleAccount,
        merchantCenter,
        merchantCenterResolved,
        feed,
        loading,
        refreshSelection,
        clearSelection,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
};
